/**
 * Carousel UI component for horizontal card scrolling with accessibility support.
 *
 * Usage:
 *   new Carousel(document.querySelector('.carousel'));
 */
export default class Carousel {
  /**
   * @param {HTMLElement} element - The root carousel element.
   */
  constructor(element) {
    if (!element) return;
    this.carousel = element;
    this.inner = this.carousel.querySelector('.carousel__inner');
    this.cards = Array.from(this.carousel.querySelectorAll('.carousel-card'));
    this.prevBtn = this.carousel.querySelector('.carousel__button--prev');
    this.nextBtn = this.carousel.querySelector('.carousel__button--next');

    this.init();
  }

  /**
   * Initialize event listeners, observer, and button state.
   * @private
   */
  init() {
    this.addEventListeners();
    this.setupObserver();
    this.updateButtons();
  }

  /**
   * Uses Intersection Observer to manage tabindex and button state for accessibility.
   * @private
   */
  setupObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const card = entry.target;
        // If the card is at least 90% visible
        const isVisible = entry.intersectionRatio >= 0.9;

        card.setAttribute('aria-hidden', !isVisible);
        card.setAttribute('tabindex', isVisible ? '0' : '-1');
        card.querySelectorAll('a, button').forEach(el => {
          el.setAttribute('tabindex', isVisible ? '0' : '-1');
        });
      });
      this.updateButtons();
    }, {
      root: this.inner,
      threshold: [0, 0.9, 1.0]
    });

    this.cards.forEach(card => observer.observe(card));
  }

  /**
   * Updates the enabled/disabled state of navigation buttons based on scroll position.
   * @private
   */
  updateButtons() {
    // Check if at the very start or end
    const scrollLeft = this.inner.scrollLeft;
    const maxScroll = this.inner.scrollWidth - this.inner.clientWidth;

    this.prevBtn.disabled = scrollLeft <= 5;
    this.nextBtn.disabled = scrollLeft >= maxScroll - 5;
  }

  /**
   * Scrolls the carousel in the given direction by one card width plus gap.
   * @param {'next'|'prev'} direction - Direction to scroll.
   */
  scroll(direction) {
    const cardWidth = this.cards[0].offsetWidth;
    const gap = 24; // Same as CSS --card-gap
    const moveAmount = cardWidth + gap;

    this.inner.scrollBy({
      left: direction === 'next' ? moveAmount : -moveAmount,
      behavior: 'smooth'
    });
  }

  /**
   * Adds event listeners for navigation buttons and manual scrolling.
   * @private
   */
  addEventListeners() {
    this.nextBtn.addEventListener('click', () => this.scroll('next'));
    this.prevBtn.addEventListener('click', () => this.scroll('prev'));

    // Also update buttons if user scrolls manually (mouse/touch)
    this.inner.addEventListener('scroll', () => this.updateButtons(), { passive: true });
  }
}
